import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Inbox,
  MessageSquare,
  Send,
  Bot,
  Mic,
  CheckCircle2,
  ArrowRight,
  Zap,
  Shield,
  Brain,
  GitPullRequest,
  FileText,
  Check,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

export default function Landing() {
  const { user } = useAuth();
  const [discountCode, setDiscountCode] = useState("");
  const [email, setEmail] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const isEarlyBird = discountCode.toUpperCase() === "EARLYBIRD";

  const handleSubscribe = async () => {
    const subscriberEmail = user?.email || email;
    if (!subscriberEmail) {
      toast.error("Please enter your email address");
      return;
    }

    setCheckoutLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          email: subscriberEmail,
          discount_code: discountCode || undefined,
          success_url: window.location.origin + "/?checkout=success",
          cancel_url: window.location.origin + "/?checkout=cancelled",
        },
      });

      if (error) {
        // Supabase client wraps non-2xx as errors
        if (error.message?.includes("503") || error.message?.includes("not configured")) {
          toast("Coming soon — we'll reach out to set you up");
          return;
        }
        throw error;
      }

      if (data?.error) {
        if (data.error.includes("not configured") || data.error.includes("Coming soon")) {
          toast("Coming soon — we'll reach out to set you up");
          return;
        }
        throw new Error(data.error);
      }

      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error("Checkout error:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" style={{ paddingTop: "env(safe-area-inset-top)" }}>
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 py-4 max-w-6xl mx-auto">
        <span className="text-lg font-bold tracking-tight">HELLA Organized</span>
        <Link
          to="/login"
          className="px-4 py-2 text-sm font-medium rounded-lg border border-border hover:bg-accent/10 transition-colors"
        >
          Sign in
        </Link>
      </nav>

      {/* Hero */}
      <section className="px-6 pt-24 pb-32 text-center max-w-4xl mx-auto">
        <div className="inline-block px-4 py-1.5 rounded-full border border-primary/30 bg-primary/5 text-primary text-sm font-medium mb-8">
          AI-powered. Not another to-do app.
        </div>
        <h1 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tight leading-[1.1]">
          Get your sh*t
          <br />
          <span className="text-primary">together.</span>
        </h1>
        <p className="mt-8 text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          Your calls, emails, texts, and meetings flow into one AI command center
          that prioritizes your day, delegates to your team, and runs an autonomous
          agent that works while you sleep.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-[6px] bg-primary text-primary-foreground font-bold text-lg uppercase tracking-[0.08em] hover:opacity-90 transition-opacity shadow-lg shadow-primary/25"
          >
            GET STARTED
            <ArrowRight className="h-5 w-5" />
          </Link>
          <a
            href="#how-it-works"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            See how it works
          </a>
        </div>
      </section>

      {/* Problem / Solution */}
      <section className="px-6 py-20 border-t border-border">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-16">
          <div>
            <h2 className="text-sm font-semibold text-red-400 uppercase tracking-wider mb-4">
              The problem
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p className="text-lg">You have 47 browser tabs open. Your inbox has 200+ unread. Three people are waiting on a reply. You missed a follow-up from Tuesday's call.</p>
              <p className="text-lg">You spend more time <em>managing</em> work than <em>doing</em> work.</p>
              <p className="text-lg">Every "productivity tool" just adds another app to check.</p>
            </div>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-green-400 uppercase tracking-wider mb-4">
              The solution
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p className="text-lg">One command center. Everything flows in. AI filters the noise. You see only what matters.</p>
              <p className="text-lg">Need to delegate? AI writes the email. Need code done? Your agent handles it. Need to follow up on a call? It's already a task.</p>
              <p className="text-lg font-medium text-foreground">You don't organize your life. Your life organizes itself.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-6 py-24 border-t border-border">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">
            Everything you need. Nothing you don't.
          </h2>
          <p className="text-center text-muted-foreground mb-16 max-w-2xl mx-auto">
            Sign in with Google and your email + calendar are instantly connected. Connect your other tools in Settings with a few clicks.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Inbox,
                title: "Smart Inbox",
                desc: "Gmail syncs every 3 minutes. AI classifies every email. Hit 'Cut the BS' and only see messages from real humans who need a reply. Everything else vanishes.",
              },
              {
                icon: Brain,
                title: "AI Chat That Actually Does Stuff",
                desc: "Not a chatbot that gives you advice. This one creates tickets, writes SOWs, manages your project database, uploads documents, and sends emails. It has tools.",
              },
              {
                icon: Send,
                title: "One-Click Delegation",
                desc: "Click delegate, type an email, AI writes a professional handoff email with all the task details. Sent from your account. You didn't write a word.",
              },
              {
                icon: Bot,
                title: "Autonomous AI Agent",
                desc: "Dispatch a task. Your Mac Mini picks it up, clones the repo, runs Claude, writes the code, creates a PR, and notifies you when it's done. While you sleep.",
              },
              {
                icon: Mic,
                title: "Meeting & Call Intelligence",
                desc: "Connect Fireflies, Plaud, Otter.ai, Google Meet, or Microsoft Teams. AI extracts every action item, follow-up, and decision. That call you had Tuesday? It's already tasks in your inbox.",
              },
              {
                icon: GitPullRequest,
                title: "GitHub Integration",
                desc: "Connect your repos with one click. Your AI agent creates branches, writes code, and opens PRs. Retry failed jobs instantly. Everything tracked in real-time.",
              },
              {
                icon: FileText,
                title: "Document Generation",
                desc: "Ask the AI to write an SOW, MSA, proposal, or any document. It generates it and gives you a download button right in the chat. Share images and PDFs with the AI for review.",
              },
              {
                icon: CheckCircle2,
                title: "Cash In System",
                desc: "Completed work gets detected automatically. Resolved tickets, closed tasks, and finished items bundle up. One click to cash them all in, earn XP, and watch the confetti fly.",
              },
              {
                icon: Shield,
                title: "Custom Connections",
                desc: "Plug in any Supabase-powered platform. The AI auto-discovers the schema and gets full read/write access. Your CRM, your database, your rules.",
              },
            ].map((f) => (
              <div
                key={f.title}
                className="rounded-xl border border-border bg-card p-6 hover:border-primary/40 transition-colors"
              >
                <f.icon className="h-6 w-6 text-primary mb-4" />
                <h3 className="text-base font-semibold mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="px-6 py-24 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16">
            Simple to set up. Impossible to outgrow.
          </h2>
          <div className="space-y-12">
            {[
              {
                num: "01",
                title: "Sign in with Google",
                desc: "One click. Gmail, Calendar, and Contacts are instantly connected. You're already in your command center.",
                icon: Zap,
              },
              {
                num: "02",
                title: "Connect your tools",
                desc: "Fireflies, Otter.ai, Plaud, GitHub, Google Meet, Microsoft Teams — connect what you use. Skip what you don't.",
                icon: MessageSquare,
              },
              {
                num: "03",
                title: "AI takes over",
                desc: "Your inbox gets classified. Your calls become tasks. Your meetings become action items. You just pick what to work on. Or delegate it. Or dispatch the agent.",
                icon: Brain,
              },
            ].map((step) => (
              <div key={step.num} className="flex gap-6 items-start">
                <div className="shrink-0 w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <span className="text-primary font-bold text-lg">{step.num}</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="px-6 py-20 border-t border-border">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-8">Works with what you already use</h2>
          <div className="flex flex-wrap items-center justify-center gap-6 text-muted-foreground">
            {["Gmail", "Google Calendar", "Google Contacts", "Fireflies", "Plaud", "Otter.ai", "Google Meet", "Microsoft Teams", "GitHub"].map((name) => (
              <div key={name} className="px-4 py-2 rounded-lg border border-border bg-card text-sm font-medium">
                {name}
              </div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground mt-6">
            Plus custom connections to any Supabase-powered platform
          </p>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="px-6 py-24 border-t border-border" style={{ background: "#0A0A0A" }}>
        <div className="max-w-2xl mx-auto">
          <h2 className="text-sm font-semibold uppercase tracking-[0.15em] text-center mb-2" style={{ color: "#CE2121" }}>
            PRICING
          </h2>
          <h3 className="text-3xl sm:text-4xl font-black text-center text-white mb-4">
            One plan. Everything included.
          </h3>
          <p className="text-center text-[#888] mb-12 max-w-lg mx-auto">
            No tiers, no feature gates, no "contact sales." You get the full command center.
          </p>

          <div
            className="rounded-2xl p-8 sm:p-10 mx-auto max-w-lg"
            style={{
              background: "linear-gradient(145deg, #141414 0%, #1a1a1a 100%)",
              border: "1px solid #2a2a2a",
              boxShadow: "0 0 80px rgba(206, 33, 33, 0.06)",
            }}
          >
            {/* Price Display */}
            <div className="text-center mb-8">
              {isEarlyBird ? (
                <>
                  <div className="flex items-center justify-center gap-3 mb-1">
                    <span className="text-2xl text-[#666] line-through font-medium">$1,000</span>
                    <span className="text-5xl sm:text-6xl font-black text-white">$299</span>
                  </div>
                  <div className="text-[#888] text-sm">per month</div>
                  <div
                    className="inline-block mt-3 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider"
                    style={{ background: "rgba(206, 33, 33, 0.15)", color: "#CE2121" }}
                  >
                    EARLY BIRD APPLIED — 70% OFF
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl sm:text-6xl font-black text-white">$1,000</span>
                  </div>
                  <div className="text-[#888] text-sm">per month</div>
                </>
              )}
            </div>

            {/* Discount Code Input */}
            <div className="mb-6">
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#666] mb-2">
                Discount Code
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value)}
                  placeholder="Enter code"
                  className="w-full px-4 py-3 rounded-lg text-sm text-white placeholder-[#555] outline-none transition-colors"
                  style={{
                    background: "#0d0d0d",
                    border: isEarlyBird ? "1px solid #CE2121" : "1px solid #2a2a2a",
                  }}
                />
                {isEarlyBird && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Check className="h-5 w-5" style={{ color: "#CE2121" }} />
                  </div>
                )}
              </div>
            </div>

            {/* Email Input (only if not logged in) */}
            {!user && (
              <div className="mb-6">
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#666] mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full px-4 py-3 rounded-lg text-sm text-white placeholder-[#555] outline-none transition-colors"
                  style={{
                    background: "#0d0d0d",
                    border: "1px solid #2a2a2a",
                  }}
                />
              </div>
            )}

            {/* Subscribe Button */}
            <button
              onClick={handleSubscribe}
              disabled={checkoutLoading}
              className="w-full py-4 rounded-lg font-bold text-base uppercase tracking-[0.1em] text-white transition-opacity hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
              style={{
                background: "linear-gradient(135deg, #CE2121 0%, #a81b1b 100%)",
                boxShadow: "0 4px 24px rgba(206, 33, 33, 0.3)",
              }}
            >
              {checkoutLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                "SUBSCRIBE"
              )}
            </button>

            {user && (
              <p className="text-center text-xs text-[#666] mt-3">
                Subscribing as {user.email}
              </p>
            )}

            {/* Feature List */}
            <div className="mt-8 pt-8" style={{ borderTop: "1px solid #2a2a2a" }}>
              <ul className="space-y-3">
                {[
                  "AI Command Center",
                  "Gmail + Calendar + Contacts sync",
                  "Live AI coding sessions (Claude Code, Codex, Gemini)",
                  "Task delegation with AI",
                  "Meeting transcript sync",
                  "Custom database connections",
                  "HELLA Projects integration",
                  "Mobile + Desktop",
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#CE2121" }} />
                    <span className="text-sm text-[#ccc]">{feature}</span>
                  </li>
                ))}
                <li className="flex items-start gap-3 pt-2" style={{ borderTop: "1px solid #1f1f1f" }}>
                  <span className="text-xs text-[#555] italic">
                    Mac agent not included — bring your own
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-24 border-t border-border">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-black mb-6">
            Get your sh*t together.
          </h2>
          <p className="text-xl text-muted-foreground mb-10 max-w-xl mx-auto">
            Every minute you spend triaging email, copying tasks between apps, and writing follow-up messages is a minute you'll never get back.
          </p>
          <button
            onClick={handleSubscribe}
            disabled={checkoutLoading}
            className="inline-flex items-center gap-2 px-10 py-4 rounded-xl font-semibold text-lg hover:opacity-90 transition-opacity text-white"
            style={{
              background: "linear-gradient(135deg, #CE2121 0%, #a81b1b 100%)",
              boxShadow: "0 4px 24px rgba(206, 33, 33, 0.3)",
            }}
          >
            {checkoutLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                Get HELLA Organized
                <ArrowRight className="h-5 w-5" />
              </>
            )}
          </button>
          <p className="mt-4 text-sm text-muted-foreground">
            {isEarlyBird ? "$299/mo with EARLYBIRD code" : "$1,000/mo — enter EARLYBIRD above for 70% off"}
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-10 border-t border-border" style={{ paddingBottom: "max(2.5rem, env(safe-area-inset-bottom))" }}>
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <span>HELLA Intelligence LLC</span>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-foreground transition-colors">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">
              Terms
            </Link>
            <a href="mailto:skyler@hellaorganized.life" className="hover:text-foreground transition-colors">
              skyler@hellaorganized.life
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
